import React, {useEffect, useState} from "react";
import {Alert, Button, List, Pagination, Popover, Select, Space, Tag} from "antd";
import {RobotOutlined, LineChartOutlined} from "@ant-design/icons";
import TimeAgo from "../helpers/TimeAgo";
import User from "../helpers/User";
import {RefreshIcon} from "../helpers/icons";
import {OrgLink} from "../helpers/OrgNavLink";
import {useTranslation} from "react-i18next";

export default function AssetLog({asset, close}) {
    const {t} = useTranslation();
    const [selection, setSelection] = useState({source: undefined, page: 1});
    const [refresh, setRefresh] = useState(0);
    const [result, setResult] = useState({log: [], total: 0, loading: true, error: false});
    const {source, page} = selection;

    useEffect(() => {
        let cancelled = false;
        setResult({log: [], total: 0, loading: true, error: false});
        api(`/api/assets/${asset.id}/log`, {params: {page, request_source: source}}).then(res => {
            if (!cancelled) setResult({log: res.data, total: Number(res.headers?.['total-entries'] || 0), loading: false, error: false});
        }).catch(() => {
            if (!cancelled) setResult({log: [], total: 0, loading: false, error: true});
        });
        return () => { cancelled = true; };
    }, [asset.id, source, page, refresh]);

    // Each modal/asset starts at the first page with all sources selected.
    useEffect(() => { setSelection({source: undefined, page: 1}); }, [asset.id]);

    const labels = {
        mcp: t('history-source-mcp', 'Agent (MCP)'),
        cli: t('history-source-cli', 'Agent (CLI)'),
        web: t('history-source-web', 'Web'),
        api: t('history-source-api', 'API'),
        system: t('history-source-system', 'System'),
        unknown: t('history-source-unknown', 'Unknown'),
    };
    const options = [
        {value: '', label: t('history-source-all', 'All sources')},
        {value: 'agent', label: t('history-source-agent', 'Agent (MCP and CLI)')},
        ...Object.entries(labels).map(([value, label]) => ({value, label})),
    ];

    return (
        <Space direction="vertical" style={{width: '100%'}}>
            <Space wrap style={{width: '100%', justifyContent: 'space-between'}}>
                <OrgLink to={`/manage/reports/downloads?search=${asset.guid}`} onClick={close}>
                    <LineChartOutlined/> {t('view-download-report', 'View Download Report')}
                </OrgLink>
                <Space wrap>
                    <Select aria-label={t('history-source', 'History source')} value={source || ''}
                        options={options} style={{minWidth: 190}}
                        onChange={value => setSelection({source: value || undefined, page: 1})}/>
                    <Button aria-label={t('button-refresh', 'Refresh')} onClick={() => setRefresh(value => value + 1)}
                        icon={<RefreshIcon/>} size="small"/>
                </Space>
            </Space>
            {result.error && <Alert type="error" showIcon message={t('history-load-error', 'History could not be loaded. Refresh to try again.')}/>}
            <List size="small" dataSource={result.log} loading={result.loading}
                renderItem={version => {
                    const agent = version.request_source === 'mcp' || version.request_source === 'cli';
                    return (
                        <List.Item>
                            <Space wrap>
                                {version.user ? <User user={version.user}/> : t('system', 'System')}
                                <Tag icon={agent ? <RobotOutlined/> : undefined} color={agent ? 'purple' : undefined}>
                                    {labels[version.request_source] || labels.unknown}
                                </Tag>
                                <Popover placement="right" content={<pre>{JSON.stringify(version.object_changes, null, 2)}</pre>}>
                                    {version.description}
                                </Popover>
                                <TimeAgo date={version.created_at}/>
                            </Space>
                        </List.Item>
                    );
                }}/>
            <Pagination current={page} pageSize={30} total={result.total} showSizeChanger={false} hideOnSinglePage
                onChange={nextPage => setSelection(current => ({...current, page: nextPage}))}/>
        </Space>
    );
}
